import React, { useEffect, useState } from 'react'
import _forEach from 'lodash/forEach'
import _find from 'lodash/find'
import _get from 'lodash/get'
import _map from 'lodash/map'
import _startCase from 'lodash/startCase'
import _uniq from 'lodash/uniq'
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger'
import Tooltip from 'react-bootstrap/lib/Tooltip'
import ClipboardButton from 'react-clipboard.js'
import SortableTableWithSearchHOC from '../../hoc/SortableTableWithSearchHOC'
import EmptyBar from '../../_library/EmptyBar'
import { getTableColumns } from '../../utils/sortableTableUtils'
import SortableTable from '../../_library/SortableTable'
import { ENV } from '../../constants/env'
import CustomerFormGuestTicketsModal from './GuestTicketsModal'
import CustomerFormInvitationModal from './InvitationModal'
import Button from '../../_library/Button'
import CustomerFormFilters from './CustomerFormFilters'
import { convertToCSV, DOWNLOAD_CSV, getCSVData } from '../../utils/csv'
import TagsField from '../../_library/TagsField'

const SortableTableWithSearch = SortableTableWithSearchHOC()

const CustomerFormsTable = ({
  data = [],
  onFormEdit,
  eventId,
  handleFromDelete,
  handleUpdateFormSubmission,
  emailTemplatesOption,
  fetchForms,
}) => {
  const [copied, setCopied] = useState({})
  const [displayGuestTicketsModal, setDisplayGuestTicketsModal] = useState(false)
  const [displInvitationsModal, setDisplInvitationsModal] = useState(false)
  const [selectedSubmission, setSelectedSubmission] = useState(null)
  const [submissions, setSubmissions] = useState({})
  const [filters, setFilters] = useState([])
  const [cardClosed, handleCardClose] = useState(true)

  useEffect(() => {
    if (Object.values(copied).find(item => item)) {
      setTimeout(() => {
        setCopied({})
      }, 800)
    }
  }, [copied])

  const tableColumns = getTableColumns([
    {
      label: 'Title',
      key: 'title',
      className: 'mw-140',
    },
    {
      label: 'Subtitle',
      key: 'subtitle',
      className: 'mw-140',
    },
    {
      label: 'Start Date',
      key: 'startDate',
      className: 'mw-140',
    },
    {
      label: 'End Date',
      key: 'endDate',
      className: 'mw-140',
    },
    {
      label: 'Form URL',
      key: 'slug',
      className: 'ws-nw',
      normalizer: (value, data) => (
        <>
          {`${ENV.API_CONSUMER}/form/${value}`}
          <ClipboardButton
            component="span"
            data-clipboard-text={`${ENV.API_CONSUMER}/form/${value}`}
            onSuccess={() => setCopied({ [data.id]: true })}
          >
            <OverlayTrigger
              placement="right"
              overlay={<Tooltip id="urlCopied">{copied[data?.id] ? 'Copied' : 'Copy Form URL'}</Tooltip>}
              trigger={['hover']}
            >
              <i className="fa fa-clipboard event-url-copy" />
            </OverlayTrigger>
          </ClipboardButton>
        </>
      ),
    },
  ])

  const rows = _map(data, item => ({
    id: _get(item, 'form.id', ''),
    slug: _get(item, 'form.slug', ''),
    title: _get(item, 'form.title', ''),
    subtitle: _get(item, 'form.subtitle') || '',
    startDate: _get(item, 'form.start_date'),
    endDate: _get(item, 'form.end_date'),
    status: _get(item, 'form.status', ''),
    description: _get(item, 'form.description') || '',
    form_data_capture: _get(item, 'form_data_capture') || [],
    fields: _map(_get(item, 'fields[0].form.group.fields') || [], item => ({
      ...item,
      disabled: item.type === 'email',
      defaultValue: typeof item.defaultValue === 'string' ? item.defaultValue : '',
    })),
    approvedEmailTemplateId: _get(item, 'form.success_email_template_id', ''),
    declinedEmailTemplateId: _get(item, 'form.reject_email_template_id', ''),
    confirmationMessage: _get(item, 'form.confirmation_message') || '',
    backgroundImage: _get(item, 'form.background_image_url') || '',
    socialImage: _get(item, 'form.social_image_url') || '',
  }))

  useEffect(() => {
    const submissionsObj = {}
    const filterOptions = {}

    _forEach(rows, row => {
      const dataCapture = _get(row, 'form_data_capture', [])
      const submissionFilterOptions = []
      const allTags = []

      _forEach(dataCapture, ({ tags }) => {
        if (tags?.length) {
          allTags.push(...tags)
        }
      })

      submissionFilterOptions.push({
        type: 'tags',
        label: 'Tags',
        options: _map(_uniq(allTags), tag => ({ value: tag, label: tag, formId: row.id })),
      })

      const submissionColumns = [
        { key: 'status', showStatusName: true, normalizer: value => _startCase(value) },
        { key: 'email' },
        {
          label: 'Tags',
          key: 'tags',
          className: 'mw-140',
          normalizer: (value, data) => (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              <TagsField
                value={value}
                onlyUnique
                onChange={tags => {
                  handleUpdateFormSubmission(data.formDataCaptureId, data.status, tags)
                }}
                controlOutside
                placeholder={'Add a tag (Enter)'}
              />
            </div>
          ),
        },
      ]

      const submissions = _map(dataCapture, item => {
        const returnObj = { ...item }
        _forEach(item?.field_capture || [], fieldData => {
          const fieldFullData = _find(row.fields, { name: fieldData.name })
          returnObj[fieldData.name] = fieldData.value

          if (fieldFullData?.type !== 'textarea' && fieldFullData?.type !== 'text') {
            if (filterOptions[fieldData.name]) {
              if (!filterOptions[fieldData.name]?.find(({ value }) => value === fieldData.value)) {
                filterOptions[fieldData.name].push({
                  value: fieldData.value,
                  label: fieldData.name !== 'email' ? _startCase(fieldData.value) : fieldData.value,
                  formId: row.id,
                })
              }
            } else {
              filterOptions[fieldData.name] = [
                {
                  value: fieldData.value,
                  label: fieldData.name !== 'email' ? _startCase(fieldData.value) : fieldData.value,
                  formId: row.id,
                },
              ]
            }

            if (!submissionFilterOptions.find(option => option.type === fieldData.name)) {
              submissionFilterOptions.push({
                type: fieldData.name,
                label: _startCase(fieldData.label || fieldData.name),
                options: filterOptions[fieldData.name],
              })
            }
          }

          if (submissionColumns.find(column => column.key === fieldData.name) === undefined) {
            submissionColumns.push({
              key: fieldData.name,
            })
          }
        })

        submissionsObj[row.id] = { submissionColumns, submissionFilterOptions }

        return {
          ...returnObj,
          fullName: `${_get(item, 'customer_first_name') || ''} ${_get(item, 'customer_last_name') || ''}`,
          formDataCaptureId: _get(item, 'field_capture[0].form_data_capture_id', ''),
        }
      }).filter(item => !!item.formDataCaptureId)

      if (submissionsObj[row.id]) {
        submissionsObj[row.id].submissions = submissions
        submissionsObj[row.id].initialSubmissions = submissions
      }
      setSubmissions(submissionsObj)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const renderRowDetailsComponent = row => {
    const rowSubmissions = submissions[row.id]?.submissions || []
    const rowInitialSubmissions = submissions[row.id]?.initialSubmissions || []
    const rowSubmissionColumns = submissions[row.id]?.submissionColumns || []
    const rowSubmissionFilterOptions = submissions[row.id]?.submissionFilterOptions || []

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <h4>
          Additional Details for <b>{row.title}</b>
        </h4>
        {row.description && (
          <span>
            <p>Description</p>
            <div
              dangerouslySetInnerHTML={{ __html: row.description }}
              style={{ padding: 10, backgroundColor: '#ffffff0f' }}
            />
          </span>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 20 }}>Form Submissions</span>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button
                className="btn btn-transparent"
                type="button"
                onClick={() => {
                  fetchForms(eventId)
                }}
              >
                <i className="fa fa-refresh" />
              </Button>
              <Button
                className="btn btn-success"
                type="button"
                disabled={!rowSubmissions?.length}
                onClick={() => {
                  const csvData = getCSVData(getTableColumns(rowSubmissionColumns), rowSubmissions, {
                    hideAdditionalColumns: true,
                  })
                  const csv = convertToCSV({ data: csvData })
                  DOWNLOAD_CSV(csv, `${row.title}_submissions.csv`)
                }}
              >
                Export CSV
              </Button>
            </div>
          </div>
          {Boolean(rowInitialSubmissions?.length) && (
            <CustomerFormFilters
              submissionFilterOptions={rowSubmissionFilterOptions}
              submissions={rowSubmissions}
              initialSubmissions={rowInitialSubmissions}
              setSubmissions={setSubmissions}
              rowId={row.id}
              filters={filters}
              setFilters={setFilters}
              cardClosed={cardClosed}
              handleCardClose={handleCardClose}
            />
          )}
          {rowSubmissions?.length ? (
            <SortableTable
              data={rowSubmissions}
              tableColumns={getTableColumns(rowSubmissionColumns)}
              enableSort={true}
              disableMobileView={true}
              sortBy={{ column: 'fullName', asc: true }}
              actions={[
                {
                  label: 'Accept',
                  className: 'btn btn-success',
                  hide: data => data.status === 'accepted',
                  disabled: data => !data.customer_id,
                  onClick: data => {
                    handleUpdateFormSubmission(data.formDataCaptureId, 'accepted', data.tags)
                  },
                },
                {
                  label: 'Decline',
                  className: 'btn btn-danger',
                  hide: data => data.status === 'declined',
                  disabled: data => !data.customer_id,
                  onClick: data => {
                    handleUpdateFormSubmission(data.formDataCaptureId, 'declined', data.tags)
                  },
                },
                {
                  label: 'Issue Guest Tickets',
                  className: 'btn btn-default btn-primary',
                  hide: data => data.status !== 'accepted',
                  onClick: data => {
                    setDisplayGuestTicketsModal(true)
                    setSelectedSubmission(data)
                  },
                },
                {
                  label: 'Invite to Buy Tickets',
                  className: 'btn btn-default btn-seablue',
                  hide: data => data.status !== 'accepted',
                  onClick: data => {
                    setDisplInvitationsModal(true)
                    setSelectedSubmission(data)
                  },
                },
              ]}
            />
          ) : (
            <EmptyBar />
          )}
        </div>
      </div>
    )
  }

  const detailsRows = rows.map(row => ({
    id: row.id,
    type: 'detailRow',
    component: renderRowDetailsComponent(row),
  }))

  if (!data || data.length === 0) {
    return <EmptyBar />
  }

  return (
    <>
      <CustomerFormGuestTicketsModal
        isModalOpen={displayGuestTicketsModal}
        closeModal={() => setDisplayGuestTicketsModal(false)}
        selectedSubmission={selectedSubmission}
      />
      <CustomerFormInvitationModal
        isModalOpen={displInvitationsModal}
        closeModal={() => setDisplInvitationsModal(false)}
        selectedSubmission={selectedSubmission}
        emailTemplatesOption={emailTemplatesOption}
      />
      <SortableTableWithSearch
        data={rows}
        tableColumns={tableColumns}
        enableSort={true}
        disableMobileView={true}
        sortBy={{ column: 'title', asc: true }}
        searchBy={['title', 'subtitle', 'slug']}
        placeholder="Search forms..."
        detailsRows={detailsRows}
        actions={[
          {
            label: 'Edit',
            className: 'btn btn-primary w-80',
            icon: 'fa fa-pencil fa-fw',
            onClick: data => onFormEdit(data),
          },
          {
            label: 'Delete',
            className: 'btn btn-danger w-80',
            icon: 'fa fa-trash fa-fw',
            onClick: data => handleFromDelete(data.id),
          },
        ]}
        onRowExpandClick={() => {
          handleCardClose(true)
        }}
      />
    </>
  )
}

export default CustomerFormsTable
